/**
 * ConversationListItem Component - HYB Shop Chat
 * Single conversation item with avatar, preview, and status
 */

import { useMemo } from 'react';
import { cn } from '../../../utils/responsive';
import { Avatar, UnreadBadge } from '../shared';
import { Trash2 } from 'lucide-react';

interface Conversation {
  conversationId: string;
  userId: string;
  userName?: string;
  phoneNumber?: string;
  isGuest?: boolean;
  guestId?: string;
  status?: string;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  unreadCount?: number;
  createdAt?: string;
  assignedAdminID?: string;
  lastMessageSender?: 'user' | 'admin';
}



interface ConversationListItemProps {
  conversation: Conversation;
  isActive?: boolean;
  isOnline?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  className?: string;
}

/**
 * Format relative time in Thai
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'เมื่อสักครู่';
  if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
  if (diffHours < 24) return `${diffHours} ชม.ที่แล้ว`;
  if (diffDays === 1) return 'เมื่อวาน';
  if (diffDays < 7) return `${diffDays} วันที่แล้ว`;

  return date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
}

export function ConversationListItem({
  conversation,
  isActive = false,
  isOnline = false,
  onClick,
  onDelete,
  className,
}: ConversationListItemProps) {
  const displayName = useMemo(() => {
    if (conversation.isGuest) {
      return `Guest #${conversation.guestId || 'ไม่ระบุ'}`;
    }
    return conversation.userName || 'ผู้ใช้';
  }, [conversation]);

  const relativeTime = useMemo(() => {
    if (!conversation.lastMessageAt) return '';
    return formatRelativeTime(conversation.lastMessageAt);
  }, [conversation.lastMessageAt]);

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3.5 text-left',
          'border-l-3 border-transparent',
          'transition-all duration-200 ease-out',
          'hover:bg-stone-50 dark:hover:bg-gray-700/50',
          'active:scale-[0.99]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500',
          'min-h-[84px]',
          isActive && 'bg-amber-50/80 dark:bg-amber-900/20',
          isActive && 'border-l-amber-500',
          isActive && 'hover:bg-amber-50 dark:hover:bg-amber-900/30',
          className
        )}
        aria-current={isActive ? 'true' : undefined}
      >
        {/* Avatar with real-time online status */}
        <Avatar
          name={displayName}
          size="md"
          isGuest={conversation.isGuest}
          isOnline={isOnline}
          className="mt-0.5 flex-shrink-0"
        />

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          {/* Top Row: Name */}
          <div className="flex items-center justify-between gap-2">
            <span className={cn(
              'text-sm font-semibold truncate flex-1',
              'text-stone-900 dark:text-white',
              isActive && 'text-amber-900 dark:text-amber-100'
            )}>
              {displayName}
            </span>
          </div>

          {/* Phone Number */}
          {/* Phone Number */}
          {!conversation.isGuest && conversation.phoneNumber && (
            <p className="text-xs text-stone-500 dark:text-gray-400 truncate">
              {conversation.phoneNumber}
            </p>
          )}

          {/* Message Preview - Now shows 2 lines for better readability */}
          <p className={cn(
            'text-xs line-clamp-2',
            'text-stone-600 dark:text-gray-300',
            'leading-relaxed',
            'break-words',
            (conversation.unreadCount ?? 0) > 0 && 'font-medium'
          )}>
            {conversation.lastMessagePreview || 'ยังไม่มีข้อความ'}
          </p>

          {/* Bottom Row: Time + Unread */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-stone-400 dark:text-gray-500 truncate">
              {relativeTime}
            </span>
            {conversation.unreadCount && conversation.unreadCount > 0 && (
              <UnreadBadge count={conversation.unreadCount} size="sm" />
            )}
          </div>
        </div>
      </button>

      {/* Delete Button (Visible on hover) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (window.confirm('ยืนยันระบบลบการสนทนานี้หรือไม่?')) {
            onDelete?.();
          }
        }}
        className={cn(
          'absolute right-2 top-2 p-1.5 rounded-lg',
          'text-stone-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
          'transition-all duration-200',
          'opacity-0 group-hover:opacity-100 focus:opacity-100'
        )}
        title="ลบการสนทนา"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default ConversationListItem;
